/* eslint-disable max-len */
/* eslint-disable security/detect-object-injection */

import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CardHeader from '../../../components/CardHeader';
import Loader from '../../../components/Loader';
import Toast from '../../../components/Toast';
import DropDownComponent from '../../StudentMaster/Directory/Components/AddStudentComponents/DropDownComponent';
import MultiSelect from '../../../components/MultiSelect';
import GlobalContext from '../../../components/Context/GloablContext';
import { dataService } from '../../../services/data.services';
import { GetBranchId } from '../../../CommonMethods/GetBranchId';
import { CognitoData } from '../../../CommonMethods/CognitoData';
import ViewSkeleton from './Shimmers/ViewSkeleton';

const NOTIFY_GROUP_ORDER = ['Institution Admin', 'Institution User', 'Branch User'];
const NOTIFY_ROLE_KEY_MAP = {
    'Institution Admin': 'institution-admin',
    'Institution User': 'institution-user',
    'Branch User': 'branch-user',
};
const NOTIFY_GROUP_LABEL_MAP = Object.fromEntries(
    Object.entries(NOTIFY_ROLE_KEY_MAP).map(([label, key]) => [key, label])
);

const SettingDescription = ({ label, helpText, required = false }) => (
    <div className="min-w-0 flex-1" style={{ flexBasis: '360px', 'text-align':'right' }}>
        <label className="text-f7 font-normal text-[#031624]">
            {label}{required && <span className="req-fld-astrk">*</span>}
        </label>
        {helpText && <p className="text-xs font-normal leading-5 text-[#647783]">({helpText})</p>}
    </div>
);

const OnlinePayments = () => {
    const { setToastmessage } = useContext(GlobalContext);
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(true);
    const [submitSelected, setSubmitSelected] = useState(false);
    const [toastErrorMessage, setToastErrorMessage] = useState('');
    const [ledgerList, setLedgerList] = useState([]);
    const [notifyOptions, setNotifyOptions] = useState([]);
    const [notifyOptionsLoaded, setNotifyOptionsLoaded] = useState(false);
    const [pendingNotifyUserIds, setPendingNotifyUserIds] = useState(null);
    const [state, setState] = useState({
        online_payments_enabled: false,
        payment_gateway_name: '',
        default_ledger: '',
        default_ledger_id: '',
        show_pay_now: false,
        partial_payment: false,
        payment_failure_notification: true,
        notify_users: [],
    });

    const handleDropDownValue = (key, value) => {
        setState((prev) => ({
            ...prev,
            ...(key === 'default_ledger'
                ? {
                    default_ledger_id: value?._id,
                    default_ledger: value?.parent_group_name_composite_key,
                }
                : {
                    [key]: value,
                }
            ),
        }));
    };

    const handleNotifySelect = (item, idKey) => {
        setState((prev) => {
            const selected = prev.notify_users;
            const itemId = item._id || item[idKey];
            const idx = selected.map((val) => val[idKey]).indexOf(itemId);
            const notify_users = idx === -1
                ? [...selected, { [idKey]: item._id, name: item.name, role_group: item.role_group }]
                : selected.filter((_, i) => i !== idx);
            return { ...prev, notify_users };
        });
    };

    const fetchNotifyOptions = async () => {
        try {
            const user = await CognitoData();
            const institution_id = user.idToken.payload['custom:institution_id'];
            const branch_id = await GetBranchId();

            const mapUsers = (res, group) => (res?.data?.data?.data || []).map((u) => ({
                _id: u._id,
                name: `${u.first_name || ''} ${u.last_name || ''}`.trim(),
                role_group: group,
            }));

            const [adminRes, instUserRes, branchUserRes] = await Promise.all([
                dataService.GetAPI(`users?role=settings&user_type=institution-admin&institution_id=${institution_id}`),
                dataService.GetAPI(`users?role=settings&institution_id=${institution_id}&user_type=institution-user&page_number=1`),
                dataService.GetAPI(`users?role=settings&user_type=branch-user&branch_id=${branch_id}&page_number=1`),
            ]);

            setNotifyOptions([
                ...mapUsers(adminRes, 'Institution Admin'),
                ...mapUsers(instUserRes, 'Institution User'),
                ...mapUsers(branchUserRes, 'Branch User'),
            ]);
        } catch (error) {
            console.log('error', error);
        } finally {
            setNotifyOptionsLoaded(true);
        }
    };

    useEffect(() => {
        if (notifyOptionsLoaded && pendingNotifyUserIds) {
            const notify_users = Object.entries(pendingNotifyUserIds).flatMap(([roleKey, ids]) => {
                const groupLabel = NOTIFY_GROUP_LABEL_MAP[roleKey];
                return notifyOptions
                    .filter((opt) => opt.role_group === groupLabel && ids.includes(opt._id))
                    .map((opt) => ({ user_id: opt._id, name: opt.name, role_group: opt.role_group }));
            });
            setState((prev) => ({ ...prev, notify_users }));
            setPendingNotifyUserIds(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [notifyOptionsLoaded, pendingNotifyUserIds]);

    const fetchLedgers = async () => {
        try {
            const branch_id = await GetBranchId();
            let searchValue = encodeURIComponent("Assets > Cash & Bank > Bank Accounts >");
            const res = await dataService.GetAPI(`groups-and-ledgers?branch_id=${branch_id}&type=ledger&search=${searchValue}&status=active&filter=true&role=collect-fee`);
            return res?.data?.data?.data || [];
        } catch (error) {
            console.log('error', error);
            return [];
        }
    };

    const fetchBranchSettings = async (ledgers) => {
        try {
            const branch_id = await GetBranchId();
            const res = await dataService.GetAPI(`branch/settings?branch_id=${branch_id}`);
            const data = res?.data?.data?.settings || res?.data?.data;
            if (data) {
                const matchedLedger = ledgers.find((ledger) => ledger._id === data.default_ledger);
                setState((prev) => ({
                    ...prev,
                    online_payments_enabled: !!data.online_payments_enabled,
                    payment_gateway_name: data.payment_gateway_name || '',
                    default_ledger_id: data.default_ledger || '',
                    default_ledger: matchedLedger?.parent_group_name_composite_key || '',
                    show_pay_now: !!data.show_pay_now,
                    partial_payment: !!data.partial_payment,
                    payment_failure_notification: true,
                }));
                if (data.payment_fail_notify_users) {
                    setPendingNotifyUserIds(data.payment_fail_notify_users);
                }
            }
        } catch (error) {
            console.log('error', error);
        }
    };

    useEffect(() => {
        (async () => {
            const ledgers = await fetchLedgers();
            setLedgerList(ledgers);
            await Promise.all([
                fetchBranchSettings(ledgers),
                fetchNotifyOptions(),
            ]);
            setPageLoading(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitSelected(true);
        if (state.notify_users.length === 0) {
            setToastErrorMessage('Please fill in all the mandatory fields');
            return;
        }
        if (state.default_ledger === '') {
            setToastErrorMessage('Please fill in all the mandatory fields');
            return;
        }
        setLoading(true);
        try {
            const branch_id = await GetBranchId();
            const payment_fail_notify_users = state.notify_users.reduce((acc, user) => {
                const roleKey = NOTIFY_ROLE_KEY_MAP[user.role_group];
                acc[roleKey] = [...(acc[roleKey] || []), user.user_id];
                return acc;
            }, {});
            const payload = {
                branch_id,
                settings: {
                    online_payments_enabled: state.online_payments_enabled,
                    payment_gateway_name: state.payment_gateway_name,
                    show_pay_now: state.show_pay_now,
                    default_ledger: state.default_ledger_id,
                    partial_payment: state.partial_payment,
                    payment_fail_notification: true,
                    payment_fail_notify_users,
                },
            };
            await dataService.PutAPI(payload, 'branch/settings');
            setToastmessage('Online Payments settings saved successfully');
        } catch (error) {
            setToastErrorMessage(error?.response?.data?.message || 'Something went wrong');
        } finally {
            setLoading(false);
        }
    };

    const formContent = (
        <div className="w-full py-4 pr-4 pl-2">
            <form className="w-full">
                {!state.online_payments_enabled && (
                    <div class="flex justify-center items-center">
                        <div class="flex justify-center items-center gap-2.5 pl-[0.8125rem] pr-[0.8125rem] p-2 rounded-sm border border-[#c2dcef] bg-[#eef7fe] text-black text-center leading-6" style={{'width': '770px'}}>
                            Online payments not enabled for this branch. Please contact Skuteq support for more details.
                        </div>
                    </div>)}
                {state.online_payments_enabled && (
                    <>
                        <div className="mb-5 flex flex-wrap items-start justify-between gap-x-8 gap-y-2">
                            <SettingDescription
                                label="Assigned payment gateway"
                                helpText="The payment provider currently configured for this branch"
                            />
                            <div className="w-full max-w-[500px] shrink-0">
                                <p className="text-f7 font-medium text-[#031624]">{state.online_payments_enabled ? state.payment_gateway_name : 'Not enabled'}</p>
                            </div>
                        </div>
                        <div className="mb-5 flex flex-wrap items-start justify-between gap-x-8 gap-y-2">
                            <SettingDescription
                                label="Select default ledger for online payment collections"
                                helpText="All successful online fee payments will be recorded under the selected bank account ledger"
                            />
                            <div className="w-full max-w-[500px] shrink-0">
                                <DropDownComponent
                                    divClassName={'relative w-[70%]'}
                                    showErrorBorder={submitSelected && (state.default_ledger === '' || state.default_ledger === undefined)}
                                    isMandatoryField={true}
                                    options={ledgerList}
                                    dropdownkey={'default_ledger'}
                                    handleDropDownValue={handleDropDownValue}
                                    placeholder={'Select a bank account Ledger'}
                                    selectedvalue={state.default_ledger !== undefined && state.default_ledger !== '' ? state.default_ledger.substring(state.default_ledger.lastIndexOf('>') + 1).trim() : state.default_ledger}
                                    title={state.default_ledger}
                                    obj={'parent_group_name_composite_key'}
                                />
                            </div>
                        </div>
                        <div className="mb-5 flex flex-wrap items-start justify-between gap-x-8 gap-y-2">
                            <SettingDescription
                                label="Show 'Pay Now' option on invoices in Skuteq Student App"
                                helpText="If enabled, parents can make an online payment for invoices from the Skuteq Student App"
                            />
                            <div className="status custom-toggle flex w-full max-w-[500px] shrink-0 items-center">
                                <label className="switch">
                                    <input
                                        type="checkbox"
                                        checked={state.show_pay_now}
                                        onChange={(e) => setState((prev) => ({ ...prev, show_pay_now: e.target.checked }))}
                                    />
                                    <span className="slider round"></span>
                                </label>
                                <p className="ml-1.5"><span>{state.show_pay_now ? 'Enabled' : 'Disabled'}</span></p>
                            </div>
                        </div>
                        <div className="mb-5 flex flex-wrap items-start justify-between gap-x-8 gap-y-2">
                            <SettingDescription
                                label="Allow partial online payments for invoices in Skuteq Student App"
                                helpText="If enabled, parents can make a partial payment for invoices in the Skuteq Student App"
                            />
                            <div className="status custom-toggle flex w-full max-w-[500px] shrink-0 items-center">
                                <label className="switch">
                                    <input
                                        type="checkbox"
                                        checked={state.partial_payment}
                                        onChange={(e) => setState((prev) => ({ ...prev, partial_payment: e.target.checked }))}
                                    />
                                    <span className="slider round"></span>
                                </label>
                                <p className="ml-1.5"><span>{state.partial_payment ? 'Enabled' : 'Disabled'}</span></p>
                            </div>
                        </div>
                        <div className="mb-5 flex flex-wrap items-start justify-between gap-x-8 gap-y-2">
                            <SettingDescription
                                label="Select users to notify when an online payment fails"
                                helpText="Select one or more institution admins, institution users, or branch users to receive a mail notification with the failed payment details."
                            />
                            <div className="w-full max-w-[500px] shrink-0">
                                <MultiSelect
                                    dropdown={notifyOptions}
                                    selected={state.notify_users}
                                    handleSelect={handleNotifySelect}
                                    id="user_id"
                                    name="name"
                                    type="User"
                                    groupBy="role_group"
                                    groupOrder={NOTIFY_GROUP_ORDER}
                                    submitSelected={submitSelected}
                                    setsubmitSelected={setSubmitSelected}
                                    showErrorBorder={submitSelected && state.notify_users.length === 0}
                                    width="w-[70%]"
                                />
                            </div>
                        </div>
                    </>
                )}
            </form>
        </div>
    );

    return (
        <CardHeader noPadding>
            <div className='mainDataCntnrTop shadow-s13'>
                <h5 class="pageHeader">Online Payments</h5>
                {state.online_payments_enabled && <>
                    <div class="flex items-center">
                        <button type="button" class="cancl-btn" onClick={() => navigate(-1)}>Cancel</button>
                        <button
                            type="submit"
                            style={{ width: loading ? '96px' : 'auto' }}
                            class="actn-btn onboardInstitution"
                            onClick={handleSubmit}
                        >
                            {!loading ? 'Save' : <span>{<Loader />}</span>}
                        </button>
                    </div>
                </>}
            </div>
            <div className='relative mainDataCntnrPageDiv scrollBlue_thick'>
                {pageLoading ? <ViewSkeleton /> : formContent}
            </div>
            {toastErrorMessage !== '' &&
                <Toast message={toastErrorMessage} type="error" setToastmessage={setToastErrorMessage} />
            }
        </CardHeader>
    );
};

export default OnlinePayments;
